import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Trash2, Loader2, Image as ImageIcon, Plus, Eye, EyeOff, Pencil, Check, Crop } from "lucide-react";
import { toast } from "sonner";
import ImageCropModal from "./ImageCropModal";
import { BANNER_ASPECT_OPTIONS, aspectInfo } from "../utils/bannerAspect";
import { confirmDialog } from "../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  { key: "offers", label: "⚡ العروضات", color: "#F5A623" },
  { key: "bestsellers", label: "⭐ الأكثر مبيعاً", color: "#2D4A3E" },
  { key: "orderwin", label: "🎁 اطلب واربح", color: "#E85D45" },
];

const MAX_IMAGES_PER_SECTION = 5;

const BannersManager = () => {
  const [banners, setBanners] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploadingSection, setUploadingSection] = useState(null);
  const [pendingBanner, setPendingBanner] = useState(null); // {file, sectionKey}
  const [editingTitle, setEditingTitle] = useState(null); // sectionKey
  const [titleDraft, setTitleDraft] = useState("");
  const fileRefs = useRef({});

  const fetchBanners = async () => {
    try {
      const [bRes, sRes] = await Promise.all([
        // include_hidden=true so the admin sees both visible AND hidden
        // banners in the management grid (toggling visibility is now
        // per-banner, not just per-section).
        axios.get(`${API}/banners?include_hidden=true`),
        axios.get(`${API}/admin/settings/sections`).catch(() => ({ data: { sections: {} } })),
      ]);
      setBanners(bRes.data);
      setSettings(sRes.data?.sections || {});
    } catch {
      toast.error("فشل تحميل الصور");
    } finally {
      setLoading(false);
    }
  };

  const updateBanner = async (bannerId, patch) => {
    try {
      const token = localStorage.getItem("jumlaty_token");
      await axios.patch(`${API}/admin/banners/${bannerId}`, patch, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchBanners();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل تحديث البنر");
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const toggleVisibility = async (sectionKey) => {
    const current = settings[sectionKey];
    const next = !(current?.visible ?? true);
    try {
      const { data } = await axios.patch(`${API}/admin/settings/sections`, {
        sections: { [sectionKey]: { visible: next } },
      });
      setSettings(data.sections || settings);
      toast.success(next ? "تم إظهار القسم للزبون" : "تم إخفاء القسم عن الزبون");
    } catch {
      toast.error("فشل تغيير الحالة");
    }
  };

  const saveTitle = async (sectionKey) => {
    const t = titleDraft.trim();
    if (!t) { toast.error("الاسم لا يمكن أن يكون فارغاً"); return; }
    try {
      const { data } = await axios.patch(`${API}/admin/settings/sections`, {
        sections: { [sectionKey]: { title_ar: t } },
      });
      setSettings(data.sections || settings);
      setEditingTitle(null);
      toast.success("تم حفظ الاسم الجديد");
    } catch {
      toast.error("فشل حفظ الاسم");
    }
  };

  const setBannerAspect = async (sectionKey, aspectKey) => {
    try {
      const { data } = await axios.patch(`${API}/admin/settings/sections`, {
        sections: { [sectionKey]: { banner_aspect: aspectKey } },
      });
      setSettings(data.sections || settings);
      toast.success("تم تغيير حجم البانر");
    } catch {
      toast.error("فشل تغيير الحجم");
    }
  };

  const handleUpload = async (e, sectionKey) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("اختر صورة فقط");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("الصورة يجب أن تكون أقل من 5 ميغابايت");
      return;
    }
    const currentCount = banners.filter((b) => b.section === sectionKey).length;
    if (currentCount >= MAX_IMAGES_PER_SECTION) {
      toast.error(`الحد الأقصى ${MAX_IMAGES_PER_SECTION} صور لكل قسم`);
      return;
    }
    // Open crop modal first; actual upload runs from confirmCropAndUpload()
    setPendingBanner({ file, sectionKey });
  };

  const confirmCropAndUpload = async (croppedFile) => {
    if (!pendingBanner) return;
    const { sectionKey } = pendingBanner;
    setUploadingSection(sectionKey);
    try {
      const fd = new FormData();
      fd.append("file", croppedFile);
      const { data: uploadData } = await axios.post(`${API}/admin/upload-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await axios.post(`${API}/admin/banners`, {
        section: sectionKey,
        title: "",
        subtitle: "",
        color: "#2D4A3E",
        image_path: uploadData.path,
        order: 0,
      });
      toast.success("تم إضافة الصورة الإعلانية");
      fetchBanners();
      setPendingBanner(null);
    } catch (err) {
      const msg = err.response?.data?.detail || "فشل رفع الصورة";
      toast.error(typeof msg === "string" ? msg : "فشل الرفع");
    } finally {
      setUploadingSection(null);
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog("حذف هذه الصورة؟"))) return;
    try {
      await axios.delete(`${API}/admin/banners/${id}`);
      toast.success("تم الحذف");
      fetchBanners();
    } catch {
      toast.error("فشل الحذف");
    }
  };

  return (
    <div data-testid="banners-management" className="space-y-6">
      <div>
        <h3 className="font-heading font-bold text-xl text-[#111814] mb-1">
          صور إعلانية للمربعات الكبيرة
        </h3>
        <p className="text-sm text-[#5C6E64]">
          ارفع حتى <span dir="ltr" className="font-bold">5</span> صور لكل قسم — تتبدّل تلقائياً وبسحب يدوي. يمكنك إخفاء أو إظهار أي قسم للزبون، وتغيير اسمه.
        </p>
      </div>

      {SECTIONS.map((s) => {
        const sectionBanners = banners.filter((b) => b.section === s.key);
        const sectionSetting = settings[s.key] || { visible: true, title_ar: s.label, banner_aspect: "wide" };
        const isHidden = !sectionSetting.visible;
        const currentTitle = sectionSetting.title_ar || s.label;
        const currentAspectKey = sectionSetting.banner_aspect || "wide";
        const currentAspect = aspectInfo(currentAspectKey);
        const atMax = sectionBanners.length >= MAX_IMAGES_PER_SECTION;
        return (
          <div key={s.key} className={`bg-white border-2 rounded-2xl p-4 space-y-3 ${
            isHidden ? "border-[#E85D45]/40 bg-[#FFF5F3]" : "border-[#E4EBE6]"
          }`}>
            <div className="flex flex-wrap justify-between items-center gap-2">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {editingTitle === s.key ? (
                  <div className="flex items-center gap-1 flex-1">
                    <input
                      type="text"
                      value={titleDraft}
                      onChange={(e) => setTitleDraft(e.target.value)}
                      autoFocus
                      data-testid={`section-title-input-${s.key}`}
                      onKeyDown={(e) => { if (e.key === "Enter") saveTitle(s.key); if (e.key === "Escape") setEditingTitle(null); }}
                      className="flex-1 px-3 py-1.5 rounded-lg border-2 border-[#3B82F6] text-sm font-bold"
                    />
                    <button
                      onClick={() => saveTitle(s.key)}
                      data-testid={`section-title-save-${s.key}`}
                      className="p-1.5 bg-[#7ED321] hover:bg-[#6cb91d] text-white rounded-full"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <h4 className="font-bold text-[#111814] truncate" data-testid={`section-title-${s.key}`}>
                      {currentTitle}
                    </h4>
                    <button
                      onClick={() => { setEditingTitle(s.key); setTitleDraft(currentTitle); }}
                      data-testid={`section-rename-${s.key}`}
                      title="تغيير الاسم"
                      className="p-1.5 hover:bg-[#E0F2FE] rounded-full text-[#3B82F6]"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => toggleVisibility(s.key)}
                  data-testid={`section-toggle-${s.key}`}
                  title={isHidden ? "إظهار للزبون" : "إخفاء عن الزبون"}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                    isHidden
                      ? "bg-[#E85D45] hover:bg-[#c14a36] text-white"
                      : "bg-[#7ED321] hover:bg-[#6cb91d] text-white"
                  }`}
                >
                  {isHidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  {isHidden ? "مخفي" : "ظاهر"}
                </button>
                <button
                  onClick={() => fileRefs.current[s.key]?.click()}
                  disabled={uploadingSection === s.key || atMax}
                  data-testid={`add-banner-${s.key}`}
                  className="flex items-center gap-2 bg-[#E85D45] hover:bg-[#d54d35] text-white px-4 py-2 rounded-full text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={atMax ? "وصلت الحد الأقصى (5 صور). احذف صورة قبل إضافة أخرى" : "إضافة صورة"}
                >
                  {uploadingSection === s.key ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  إضافة صورة
                </button>
                <input
                  ref={(el) => (fileRefs.current[s.key] = el)}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleUpload(e, s.key)}
                  className="hidden"
                  data-testid={`upload-banner-${s.key}`}
                />
              </div>
            </div>
            <p className="text-[11px] text-[#5C6E64]">
              <span dir="ltr">{sectionBanners.length}/{MAX_IMAGES_PER_SECTION}</span> صور — تتبدّل تلقائياً للزبون كل 3.5 ثانية
              {isHidden && <span className="text-[#E85D45] font-bold mr-2">(هذا القسم مخفي عن الزبون حالياً)</span>}
            </p>

            {/* Banner aspect picker — controls both the crop modal aspect and
                the size the carousel renders on the customer's home page. */}
            <div className="bg-[#F0F9FF] border border-[#BAE6FD] rounded-xl p-2.5"
              data-testid={`section-aspect-${s.key}`}>
              <p className="text-[10px] font-bold text-[#1E3A5F] mb-1.5 flex items-center gap-1">
                <Crop className="w-3 h-3" /> حجم البانر للزبون
              </p>
              <div className="flex flex-wrap gap-1.5">
                {BANNER_ASPECT_OPTIONS.map((opt) => {
                  const on = currentAspectKey === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setBannerAspect(s.key, opt.key)}
                      data-testid={`section-aspect-${s.key}-${opt.key}`}
                      title={opt.hint}
                      className={`text-[11px] font-bold py-1 px-3 rounded-full transition-colors border ${
                        on
                          ? "bg-[#3B82F6] text-white border-[#3B82F6]"
                          : "bg-white text-[#1E3A5F] border-[#BAE6FD] hover:border-[#3B82F6]"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-[#5C6E64] mt-1.5">
                التحديد الحالي: <span className="font-bold text-[#1E3A5F]">{currentAspect.label}</span> · {currentAspect.hint}
              </p>
            </div>

            {sectionBanners.length === 0 ? (
              <div className="text-center py-8 bg-[#F3F6F4] rounded-xl">
                <ImageIcon className="w-12 h-12 text-[#BAE6FD] mx-auto mb-2" />
                <p className="text-sm text-[#5C6E64]">لا توجد صور بعد - ارفع صور لتظهر في الصفحة</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {sectionBanners.map((b, idx) => (
                  <motion.div
                    key={b.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    data-testid={`banner-${b.id}`}
                    className={`relative rounded-xl overflow-hidden border group ${
                      b.hidden ? "border-[#E85D45] opacity-60" : "border-[#E4EBE6]"
                    } bg-[#F3F6F4]`}
                    style={{ aspectRatio: currentAspect.aspectCss }}
                  >
                    {b.image_path ? (
                      <img
                        src={`${API}/files/${b.image_path}`}
                        alt={`banner ${s.label}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div
                        className="w-full h-full flex items-center justify-center"
                        style={{ backgroundColor: b.color }}
                      >
                        <ImageIcon className="w-8 h-8 text-white opacity-70" />
                      </div>
                    )}

                    {/* Top-left: editable position number — admin types
                        the order directly (1 = appears first). Falls back
                        to up/down arrows below for quick reordering. */}
                    <div className="absolute top-2 left-2 flex items-center gap-1">
                      <select
                        value={Number.isFinite(b.order) ? b.order : idx}
                        onChange={(e) => updateBanner(b.id, { order: parseInt(e.target.value, 10) })}
                        data-testid={`banner-order-select-${b.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-black/70 text-white text-[11px] font-extrabold px-2 py-1 rounded-full backdrop-blur-sm border border-white/20 cursor-pointer focus:outline-none"
                        title="تغيير ترتيب الصورة"
                      >
                        {Array.from({ length: Math.max(6, sectionBanners.length) }, (_, n) => (
                          <option key={n} value={n} className="bg-black text-white">
                            رقم {n + 1}
                          </option>
                        ))}
                      </select>
                      {b.hidden && <span className="text-[10px] bg-[#E85D45] text-white font-bold px-2 py-0.5 rounded-full">مخفي</span>}
                    </div>

                    {/* Bottom toolbar — order up/down + hide + delete */}
                    <div className="absolute bottom-1 left-1 right-1 flex items-center justify-between gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="flex gap-1">
                        <button
                          onClick={() => {
                            if (idx === 0) return;
                            const prev = sectionBanners[idx - 1];
                            updateBanner(b.id, { order: prev.order ?? idx - 1 });
                            updateBanner(prev.id, { order: b.order ?? idx });
                          }}
                          disabled={idx === 0}
                          data-testid={`banner-up-${b.id}`}
                          title="حرّك للأمام"
                          className="w-7 h-7 bg-white/90 hover:bg-white text-[#2D4A3E] rounded-full flex items-center justify-center text-xs font-extrabold disabled:opacity-40"
                        >▲</button>
                        <button
                          onClick={() => {
                            if (idx >= sectionBanners.length - 1) return;
                            const nxt = sectionBanners[idx + 1];
                            updateBanner(b.id, { order: nxt.order ?? idx + 1 });
                            updateBanner(nxt.id, { order: b.order ?? idx });
                          }}
                          disabled={idx >= sectionBanners.length - 1}
                          data-testid={`banner-down-${b.id}`}
                          title="حرّك للخلف"
                          className="w-7 h-7 bg-white/90 hover:bg-white text-[#2D4A3E] rounded-full flex items-center justify-center text-xs font-extrabold disabled:opacity-40"
                        >▼</button>
                        <button
                          onClick={() => updateBanner(b.id, { hidden: !b.hidden })}
                          data-testid={`banner-toggle-${b.id}`}
                          title={b.hidden ? "إظهار للزبون" : "إخفاء عن الزبون"}
                          className="w-7 h-7 bg-white/90 hover:bg-white text-[#2D4A3E] rounded-full flex items-center justify-center"
                        >
                          {b.hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                      <button
                        onClick={() => handleDelete(b.id)}
                        data-testid={`delete-banner-${b.id}`}
                        className="w-7 h-7 bg-[#E85D45] hover:bg-[#d54d35] text-white rounded-full flex items-center justify-center"
                        title="حذف"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Image crop modal — uses the aspect chosen for the section being uploaded to */}
      <ImageCropModal
        file={pendingBanner?.file || null}
        aspect={aspectInfo((settings[pendingBanner?.sectionKey] || {}).banner_aspect).ratio}
        onCancel={() => setPendingBanner(null)}
        onConfirm={confirmCropAndUpload}
      />
    </div>
  );
};

export default BannersManager;
